import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Download, Pencil, Trash2, FileSignature } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { SEA_EMPTY, SeaFields, downloadContractPdf, fromContract, toSeaPayload, validateSea } from "@/components/SeaForm";

function RegenerateDialog({ contract, onClose, onSaved }) {
  const [form, setForm] = useState(SEA_EMPTY);
  const [ranks, setRanks] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!contract) return;
    setForm(fromContract(contract));
    api.get(`/vessels/${contract.vessel_id}/sea-form`).then(({ data }) => setRanks(data.ranks)).catch(() => setRanks([]));
  }, [contract]);

  const submit = async (e) => {
    e.preventDefault();
    const err = validateSea(form);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      await api.put(`/sea-contracts/${contract.id}`, toSeaPayload(form));
      toast.success("Contract regenerated");
      onClose();
      onSaved();
    } catch (er) {
      toast.error(formatApiError(er));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!contract} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sea-regenerate-dialog">
        <DialogHeader>
          <DialogTitle>Edit &amp; regenerate SEA</DialogTitle>
          <DialogDescription>A new PDF replaces the current one, using the vessel owner's template.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <SeaFields form={form} setForm={setForm} ranks={ranks} vesselName={contract?.vessel_name} ownerName={contract?.owner_name} />
          <DialogFooter>
            <Button type="submit" disabled={saving} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="sea-regenerate-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Regenerate PDF"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ContractRow({ c, canManage, onEdit, onDelete }) {
  const download = () => downloadContractPdf(api, c).catch(() => toast.error("Could not download the PDF"));
  return (
    <tr className="border-t border-slate-100" data-testid={`sea-row-${c.id}`}>
      <td className="px-4 py-3.5 font-semibold text-slate-800" data-testid={`sea-seafarer-${c.id}`}>
        {c.seafarer_name}
        <div className="md:hidden text-xs font-normal text-slate-500 mt-0.5">{c.rank} · {c.wages_display}</div>
      </td>
      <td className="px-4 py-3.5 text-slate-700 hidden md:table-cell">{c.rank}</td>
      <td className="px-4 py-3.5"><Link to={`/vessels/${c.vessel_id}`} className="text-slate-700 hover:text-cyan-700">{c.vessel_name}</Link></td>
      <td className="px-4 py-3.5 text-slate-600 hidden md:table-cell">{c.owner_name}</td>
      <td className="px-4 py-3.5 text-slate-700 whitespace-nowrap hidden md:table-cell" data-testid={`sea-wages-${c.id}`}>{c.wages_display} {c.currency}</td>
      <td className="px-4 py-3.5 text-slate-500 whitespace-nowrap font-mono text-xs hidden md:table-cell">{c.generated_at ? new Date(c.generated_at).toLocaleDateString() : "—"}</td>
      <td className="px-4 py-3.5 whitespace-nowrap text-right">
        <Button size="sm" variant="ghost" onClick={download} data-testid={`sea-download-btn-${c.id}`}><Download className="w-3.5 h-3.5 mr-1" /> PDF</Button>
        {canManage && (
          <>
            <Button size="icon" variant="ghost" onClick={() => onEdit(c)} data-testid={`sea-edit-btn-${c.id}`}><Pencil className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={() => onDelete(c)} data-testid={`sea-delete-btn-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
          </>
        )}
      </td>
    </tr>
  );
}

export default function SeaContractsPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("contracts.manage");
  const [items, setItems] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [vesselId, setVesselId] = useState("all");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sea-contracts", { params: vesselId === "all" ? {} : { vessel_id: vesselId } });
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vesselId]);
  useEffect(() => {
    if (hasPermission("vessels.view")) api.get("/vessels").then(({ data }) => setVessels(data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const remove = async () => {
    const c = toDelete;
    setToDelete(null);
    try { await api.delete(`/sea-contracts/${c.id}`); toast.success("Contract deleted"); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="sea-contracts-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">Contracts</div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>SEA Contracts</h1>
          <div className="text-sm text-slate-500 mt-1">{canManage ? "Generate new contracts from a vessel's Generate SEA tab." : "All generated seafarer employment agreements."}</div>
        </div>
        <Select value={vesselId} onValueChange={setVesselId}>
          <SelectTrigger className="w-60" data-testid="sea-vessel-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="sea-vessel-filter-all">All vessels</SelectItem>
            {vessels.map((v) => <SelectItem key={v.id} value={v.id} data-testid={`sea-vessel-filter-${v.id}`}>{v.vessel_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sea-contracts-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>{[["Seafarer Name", 1], ["Rank"], ["Vessel", 1], ["Owner"], ["Wages"], ["Generated"], ["", 1]].map(([h, always]) => <th key={h} className={`px-4 py-3 text-left font-medium whitespace-nowrap ${always ? "" : "hidden md:table-cell"}`}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {!loading && items.map((c) => <ContractRow key={c.id} c={c} canManage={canManage} onEdit={setEditing} onDelete={setToDelete} />)}
            </tbody>
          </table>
        </div>
        {loading && <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div>}
        {!loading && items.length === 0 && (
          <div className="py-14 text-center" data-testid="sea-empty-state">
            <FileSignature className="w-8 h-8 text-slate-300 mx-auto" />
            <div className="text-sm text-slate-500 mt-2">No SEA contracts generated yet.</div>
          </div>
        )}
      </div>

      <RegenerateDialog contract={editing} onClose={() => setEditing(null)} onSaved={load} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="sea-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete SEA for {toDelete?.seafarer_name}?</AlertDialogTitle>
            <AlertDialogDescription>The contract and its PDF link will be removed from the list.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sea-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="sea-delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
